// Story 22.3 (FR88) — the CITS reversal of an attachment made in error.
//
// Route-shaped like 22.1's attach (the ORGANIZATION is the path parameter, the
// target user travels in the body), so it gets a file and a path helper of its
// own for the same reason `useAttachClinicianToOrg` does.
//
// INVALIDATE-AND-REFETCH on the same five keys as the attach. The reversal moves
// the same things in the opposite direction — this user's access_state and
// entitlement source, and the organization's four seat figures — and the
// preflight key goes with them, because a second look at the dialog after a
// successful reversal must read `ORG-029`, not the eligibility it just spent.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import { ADMIN_CONSOLE_QUERY_KEY } from "@/hooks/queries/useAdminConsole";
import { ADMIN_ORG_QUERY_KEY } from "@/hooks/queries/useAdminOrgDetail";
import { ADMIN_ORG_TOTALS_QUERY_KEY } from "@/hooks/queries/useAdminOrgTotals";
import { ADMIN_ORGS_QUERY_KEY } from "@/hooks/queries/useAdminOrgs";
import { ADMIN_USERS_QUERY_KEY } from "@/hooks/queries/useAdminUsers";
import { adminUserQueryKey } from "@/hooks/queries/useAdminUserDetail";
import { REVERSAL_PREFLIGHT_QUERY_KEY } from "@/hooks/queries/useReversalPreflight";
import type {
  ReverseAttachmentData,
  ReverseAttachmentRequest,
} from "@/hooks/queries/admin-users-types";

/** `POST /admin/orgs/{org_id}/reverse-attachment` — the organization is the path. */
export function reverseAttachmentPath(orgId: string): string {
  return `/admin/orgs/${encodeURIComponent(orgId)}/reverse-attachment`;
}

/**
 * Unlike the attach, the organization is already known — it is the one the
 * clinician is seated on — so it is a hook argument and only the reason is a
 * mutation variable.
 */
export function useReverseAttachment(orgId: string, userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: Omit<ReverseAttachmentRequest, "user_id">) =>
      apiFetch<ReverseAttachmentData>(reverseAttachmentPath(orgId), {
        method: "POST",
        body: { user_id: userId, ...body },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminUserQueryKey(userId) });
      void queryClient.invalidateQueries({ queryKey: [ADMIN_USERS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [ADMIN_ORGS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [ADMIN_ORG_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [ADMIN_CONSOLE_QUERY_KEY] });
      // Story 22.4, from 22.3's own review (H7). The platform org TOTALS read
      // `seat_consumed`, which this mutation moves, and every other mutation
      // that moves it — renew, extend, adjust-seats, create-organization —
      // already invalidates this key. Without it the Organizations tile keeps
      // printing the pre-reversal seat count until something unrelated
      // refetches, and the estate disagrees with itself on one screen.
      void queryClient.invalidateQueries({ queryKey: [ADMIN_ORG_TOTALS_QUERY_KEY] });
      void queryClient.invalidateQueries({
        queryKey: [REVERSAL_PREFLIGHT_QUERY_KEY],
      });
    },
  });
}
